import React, { useState, useEffect } from 'react';
import { Video, Save, ExternalLink, Eye, EyeOff, Play, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_VIDEOS = [
  { step_number: 1, step_name: "About You & Family", youtube_url: "", video_title: "", is_enabled: false },
  { step_number: 2, step_name: "Executors & Witnesses", youtube_url: "", video_title: "", is_enabled: false },
  { step_number: 3, step_name: "Asset Categories", youtube_url: "", video_title: "", is_enabled: false },
  { step_number: 4, step_name: "Define Assets", youtube_url: "", video_title: "", is_enabled: false },
  { step_number: 5, step_name: "Beneficiaries", youtube_url: "", video_title: "", is_enabled: false },
  { step_number: 6, step_name: "Exclusions", youtube_url: "", video_title: "", is_enabled: false },
  { step_number: 7, step_name: "Residual Assets", youtube_url: "", video_title: "", is_enabled: false },
  { step_number: 8, step_name: "Special Wishes", youtube_url: "", video_title: "", is_enabled: false },
  { step_number: 9, step_name: "Preview & Download", youtube_url: "", video_title: "", is_enabled: false },
];

const AdminVideoSettings = () => {
  const [videos, setVideos] = useState(DEFAULT_VIDEOS);
  const [loginVideo, setLoginVideo] = useState({ youtube_url: "", video_title: "", is_enabled: false });
  const [completionVideo, setCompletionVideo] = useState({ youtube_url: "", video_title: "", is_enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [previewLoginVideo, setPreviewLoginVideo] = useState(false);
  const [previewCompletionVideo, setPreviewCompletionVideo] = useState(false);

  useEffect(() => {
    fetchVideoSettings();
  }, []);

  const fetchVideoSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/video-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.videos && response.data.videos.length > 0) {
        setVideos(response.data.videos);
      }
      if (response.data.login_video) {
        setLoginVideo(response.data.login_video);
      }
      if (response.data.completion_video) {
        setCompletionVideo(response.data.completion_video);
      }
    } catch (error) {
      console.error('Error fetching video settings:', error);
      toast.error('Failed to load video settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/video-settings`,
        { videos, login_video: loginVideo, completion_video: completionVideo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Video settings saved successfully');
    } catch (error) {
      console.error('Error saving video settings:', error);
      toast.error('Failed to save video settings');
    } finally {
      setSaving(false);
    }
  };

  const updateVideo = (stepNumber, field, value) => {
    setVideos(prev => prev.map(v => 
      v.step_number === stepNumber ? { ...v, [field]: value } : v
    ));
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const isValidYouTubeUrl = (url) => {
    return url === '' || getYouTubeVideoId(url) !== null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <Video className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">Video Settings</h2>
            <p className="text-sm text-slate-400">Configure tutorial videos for each will creation step</p>
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
              Saving...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save size={18} />
              Save Changes
            </span>
          )}
        </Button>
      </div>

      {/* Instructions */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
          <AlertCircle size={16} />
          YouTube Video Setup Instructions
        </h3>
        <ul className="text-sm text-slate-400 space-y-1 ml-6 list-disc">
          <li>Set your YouTube video visibility to <strong className="text-white">Unlisted</strong> for privacy</li>
          <li>Paste the full YouTube URL or just the video ID</li>
          <li>Videos are embedded with reduced branding and escape routes</li>
          <li>Enable the toggle to show the video to clients</li>
          <li>Supported formats: youtube.com/watch?v=XXX, youtu.be/XXX, or just the video ID</li>
        </ul>
      </div>

      {/* Login Page Video Section */}
      <div className={`bg-slate-800/50 border rounded-xl p-5 transition-colors ${
        loginVideo.is_enabled ? 'border-blue-500/50' : 'border-slate-700'
      }`}>
        <div className="flex items-start gap-4">
          {/* Icon Badge */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            loginVideo.is_enabled ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-700 text-slate-400'
          }`}>
            <Play size={20} />
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-4">
            {/* Title & Toggle */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-white">Client Login Page Video</h3>
                <p className="text-xs text-slate-500">Displayed on the partner's client login page</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${loginVideo.is_enabled ? 'text-blue-400' : 'text-slate-500'}`}>
                  {loginVideo.is_enabled ? 'Enabled' : 'Disabled'}
                </span>
                <Switch
                  checked={loginVideo.is_enabled}
                  onCheckedChange={(checked) => setLoginVideo(prev => ({ ...prev, is_enabled: checked }))}
                />
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* YouTube URL */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">YouTube URL</Label>
                <div className="relative">
                  <Input
                    type="text"
                    value={loginVideo.youtube_url}
                    onChange={(e) => setLoginVideo(prev => ({ ...prev, youtube_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                    className={`bg-slate-900/50 border-slate-600 text-white pr-10 ${
                      loginVideo.youtube_url && !isValidYouTubeUrl(loginVideo.youtube_url) ? 'border-red-500 focus:border-red-500' : ''
                    }`}
                  />
                  {loginVideo.youtube_url && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isValidYouTubeUrl(loginVideo.youtube_url) ? (
                        <CheckCircle size={16} className="text-emerald-400" />
                      ) : (
                        <AlertCircle size={16} className="text-red-400" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Video Title */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Video Title (Optional)</Label>
                <Input
                  type="text"
                  value={loginVideo.video_title}
                  onChange={(e) => setLoginVideo(prev => ({ ...prev, video_title: e.target.value }))}
                  placeholder="Welcome to WillKart"
                  className="bg-slate-900/50 border-slate-600 text-white"
                />
              </div>
            </div>

            {/* Preview & Actions */}
            {getYouTubeVideoId(loginVideo.youtube_url) && (
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => setPreviewLoginVideo(!previewLoginVideo)}
                  className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors"
                >
                  {previewLoginVideo ? (
                    <>
                      <EyeOff size={14} />
                      Hide Preview
                    </>
                  ) : (
                    <>
                      <Eye size={14} />
                      Preview Video
                    </>
                  )}
                </button>
                <a
                  href={`https://www.youtube.com/watch?v=${getYouTubeVideoId(loginVideo.youtube_url)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors"
                >
                  <ExternalLink size={14} />
                  Open in YouTube
                </a>
              </div>
            )}

            {/* Video Preview */}
            {previewLoginVideo && getYouTubeVideoId(loginVideo.youtube_url) && (
              <div className="mt-4 rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${getYouTubeVideoId(loginVideo.youtube_url)}?rel=0&modestbranding=1`}
                  title={loginVideo.video_title || "Login Page Video"}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Post-Will Completion Video Section */}
      <div className={`bg-slate-800/50 border rounded-xl p-5 transition-colors ${
        completionVideo.is_enabled ? 'border-amber-500/50' : 'border-slate-700'
      }`}>
        <div className="flex items-start gap-4">
          {/* Icon Badge */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            completionVideo.is_enabled ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'
          }`}>
            <CheckCircle size={20} />
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-4">
            {/* Title & Toggle */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-white">Post-Will Completion Video</h3>
                <p className="text-xs text-slate-500">Full-screen video shown after client completes their will</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${completionVideo.is_enabled ? 'text-amber-400' : 'text-slate-500'}`}>
                  {completionVideo.is_enabled ? 'Enabled' : 'Disabled'}
                </span>
                <Switch
                  checked={completionVideo.is_enabled}
                  onCheckedChange={(checked) => setCompletionVideo(prev => ({ ...prev, is_enabled: checked }))}
                  data-testid="completion-video-toggle"
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-xs text-amber-200">
                <strong>Behavior:</strong> When enabled, a full-screen video modal will appear after the client clicks 
                "Complete & Save Will". The client must interact with the modal (close or continue) before being 
                redirected to their dashboard. The video is embedded with restricted controls to discourage direct YouTube access.
              </p>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* YouTube URL */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">YouTube URL</Label>
                <div className="relative">
                  <Input
                    type="text"
                    value={completionVideo.youtube_url}
                    onChange={(e) => setCompletionVideo(prev => ({ ...prev, youtube_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                    className={`bg-slate-900/50 border-slate-600 text-white pr-10 ${
                      completionVideo.youtube_url && !isValidYouTubeUrl(completionVideo.youtube_url) ? 'border-red-500 focus:border-red-500' : ''
                    }`}
                    data-testid="completion-video-url"
                  />
                  {completionVideo.youtube_url && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isValidYouTubeUrl(completionVideo.youtube_url) ? (
                        <CheckCircle size={16} className="text-emerald-400" />
                      ) : (
                        <AlertCircle size={16} className="text-red-400" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Video Title */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Video Title (Optional)</Label>
                <Input
                  type="text"
                  value={completionVideo.video_title}
                  onChange={(e) => setCompletionVideo(prev => ({ ...prev, video_title: e.target.value }))}
                  placeholder="Congratulations on completing your will!"
                  className="bg-slate-900/50 border-slate-600 text-white"
                  data-testid="completion-video-title"
                />
              </div>
            </div>

            {/* Preview & Actions */}
            {getYouTubeVideoId(completionVideo.youtube_url) && (
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => setPreviewCompletionVideo(!previewCompletionVideo)}
                  className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors"
                >
                  {previewCompletionVideo ? (
                    <>
                      <EyeOff size={14} />
                      Hide Preview
                    </>
                  ) : (
                    <>
                      <Eye size={14} />
                      Preview Video
                    </>
                  )}
                </button>
                <a
                  href={`https://www.youtube.com/watch?v=${getYouTubeVideoId(completionVideo.youtube_url)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors"
                >
                  <ExternalLink size={14} />
                  Open in YouTube
                </a>
              </div>
            )}

            {/* Video Preview */}
            {previewCompletionVideo && getYouTubeVideoId(completionVideo.youtube_url) && (
              <div className="mt-4 rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${getYouTubeVideoId(completionVideo.youtube_url)}?rel=0&modestbranding=1&controls=0`}
                  title={completionVideo.video_title || "Will Completion Video"}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 border-t border-slate-700"></div>
        <span className="text-xs text-slate-500 font-medium">WILL CREATION STEPS</span>
        <div className="flex-1 border-t border-slate-700"></div>
      </div>

      {/* Video Settings Grid */}
      <div className="space-y-4">
        {videos.map((video) => {
          const videoId = getYouTubeVideoId(video.youtube_url);
          const isValid = isValidYouTubeUrl(video.youtube_url);
          
          return (
            <div
              key={video.step_number}
              className={`bg-slate-800/50 border rounded-xl p-5 transition-colors ${
                video.is_enabled ? 'border-emerald-500/50' : 'border-slate-700'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Step Number Badge */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${
                  video.is_enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
                }`}>
                  {video.step_number}
                </div>

                {/* Main Content */}
                <div className="flex-1 space-y-4">
                  {/* Step Name & Toggle */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-white">{video.step_name}</h3>
                      <p className="text-xs text-slate-500">Step {video.step_number} of 9</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-medium ${video.is_enabled ? 'text-emerald-400' : 'text-slate-500'}`}>
                        {video.is_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <Switch
                        checked={video.is_enabled}
                        onCheckedChange={(checked) => updateVideo(video.step_number, 'is_enabled', checked)}
                      />
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* YouTube URL */}
                    <div className="space-y-2">
                      <Label className="text-slate-300 text-sm">YouTube URL</Label>
                      <div className="relative">
                        <Input
                          type="text"
                          value={video.youtube_url}
                          onChange={(e) => updateVideo(video.step_number, 'youtube_url', e.target.value)}
                          placeholder="https://youtube.com/watch?v=..."
                          className={`bg-slate-900/50 border-slate-600 text-white pr-10 ${
                            !isValid ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                        />
                        {video.youtube_url && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {isValid ? (
                              <CheckCircle size={16} className="text-emerald-400" />
                            ) : (
                              <AlertCircle size={16} className="text-red-400" />
                            )}
                          </div>
                        )}
                      </div>
                      {!isValid && (
                        <p className="text-xs text-red-400">Invalid YouTube URL format</p>
                      )}
                    </div>

                    {/* Video Title */}
                    <div className="space-y-2">
                      <Label className="text-slate-300 text-sm">Video Title (Optional)</Label>
                      <Input
                        type="text"
                        value={video.video_title}
                        onChange={(e) => updateVideo(video.step_number, 'video_title', e.target.value)}
                        placeholder={`How to complete ${video.step_name}`}
                        className="bg-slate-900/50 border-slate-600 text-white"
                      />
                    </div>
                  </div>

                  {/* Preview & Actions */}
                  {videoId && (
                    <div className="flex items-center gap-3 pt-2">
                      <button
                        onClick={() => setPreviewVideo(previewVideo === video.step_number ? null : video.step_number)}
                        className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors"
                      >
                        {previewVideo === video.step_number ? (
                          <>
                            <EyeOff size={14} />
                            Hide Preview
                          </>
                        ) : (
                          <>
                            <Eye size={14} />
                            Preview Video
                          </>
                        )}
                      </button>
                      <a
                        href={`https://www.youtube.com/watch?v=${videoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors"
                      >
                        <ExternalLink size={14} />
                        Open in YouTube
                      </a>
                    </div>
                  )}

                  {/* Video Preview */}
                  {previewVideo === video.step_number && videoId && (
                    <div className="mt-4 rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`}
                        title={video.video_title || video.step_name}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminVideoSettings;
